import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { ShoppingBag, Trash2, Minus, Plus } from 'lucide-react'
import { Link, useNavigate } from 'react-router-dom'
import { useCheckoutBundleStore } from '../store/checkoutBundleStore'
import { trackEvent, buildBundleParams } from '../utils/metaPixel'
import UiButton from '../components/ui/UiButton'

export default function CartPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { items, removeTier, updateQuantity, getTotalMonthlyPrice, clearBundle } =
    useCheckoutBundleStore()

  const total = getTotalMonthlyPrice()

  useEffect(() => {
    if (items.length > 0) {
      trackEvent('ViewCart', buildBundleParams(items))
    }
  }, [items])

  return (
    <div className="max-w-5xl mx-auto px-4 py-12 min-h-[calc(100vh-10rem)]">
      <div className="bg-brand-cream/95 rounded-3xl p-6 md:p-10 shadow-xl">
        <h1 className="font-display text-3xl text-brand-blue mb-6">{t('cart.title')}</h1>

        {items.length === 0 ? (
          <div className="text-center py-12">
            <ShoppingBag size={48} className="mx-auto text-gray-400 mb-4" />
            <p className="text-gray-700 mb-2">{t('cart.empty')}</p>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              {t('checkout.bundleEmpty')}
            </p>
            <Link
              to="/"
              className="inline-block bg-brand-orange text-white rounded-full px-6 py-2 font-bold hover:bg-brand-gold transition"
            >
              {t('common.goHome')}
            </Link>
          </div>
        ) : (
          <div className="space-y-4">
            {items.map((item) => (
              <div
                key={item.slug}
                className="flex items-center gap-4 bg-white rounded-2xl p-4 shadow-sm"
              >
                <img
                  src={item.image}
                  alt=""
                  className="w-16 h-16 md:w-20 md:h-20 object-contain rounded-xl"
                />
                <div className="flex-1 min-w-0">
                  <h3 className="font-bold text-brand-blue truncate">{item.name}</h3>
                  <p className="text-sm text-gray-600">
                    {item.priceMonthly} zł / {t('subscription.month')}
                  </p>
                </div>

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.slug, -1)}
                    className="p-2 rounded-full hover:bg-brand-cream text-brand-blue"
                    aria-label={t('cart.decreaseQuantity', 'Zmniejsz ilość')}
                  >
                    <Minus size={16} />
                  </button>
                  <span className="w-8 text-center font-bold">{item.quantity}</span>
                  <button
                    type="button"
                    onClick={() => updateQuantity(item.slug, 1)}
                    className="p-2 rounded-full hover:bg-brand-cream text-brand-blue"
                    aria-label={t('cart.increaseQuantity', 'Zwiększ ilość')}
                  >
                    <Plus size={16} />
                  </button>
                </div>

                <div className="text-right min-w-[80px]">
                  <p className="font-bold text-brand-blue">
                    {item.priceMonthly * item.quantity} zł
                  </p>
                </div>

                <button
                  type="button"
                  onClick={() => removeTier(item.slug)}
                  className="p-2 text-gray-400 hover:text-red-600 transition"
                  aria-label={t('cart.remove')}
                >
                  <Trash2 size={18} />
                </button>
              </div>
            ))}

            <div className="flex flex-col sm:flex-row justify-between items-center gap-4 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={clearBundle}
                className="text-sm text-gray-500 hover:text-red-600 underline"
              >
                {t('cart.clearBundle', 'Wyczyść koszyk')}
              </button>
              <div className="flex items-center gap-4">
                <span className="text-xl font-bold text-brand-blue">{t('cart.total')}</span>
                <span className="text-2xl font-bold text-brand-orange">{total} zł</span>
              </div>
            </div>

            <UiButton
              type="button"
              size="lg"
              fullWidth
              onClick={() => navigate('/checkout')}
            >
              {t('cart.checkout')}
            </UiButton>
          </div>
        )}
      </div>
    </div>
  )
}
